/**
 * Breeder Information Section Component
 * 
 * This component contains only the basic breeder information input:
 * - Total birds input with validation
 * - Save/cancel functionality
 * - Edit mode management
 * - Validation messages
 * 
 * This is the basic breeder info component for the Personal Info tab.
 */

import React, { useState, useEffect } from 'react';
import { SaveParticipant } from '../../api/actions';

/**
 * BreederInfoInput - Basic breeder information input component
 * 
 * @param {Object} props - Component props
 * @param {Object} props.participant - Current participant object
 * @param {Function} props.onMessage - Message callback for user feedback
 * @param {Function} props.onParticipantUpdate - Callback when participant data changes
 * @param {boolean} props.isEditing - Whether component is in edit mode
 * @param {Function} props.onEditingChange - Callback when editing state changes
 * @param {boolean} props.saving - Whether save operation is in progress
 * @param {Function} props.setSaving - Function to set saving state
 * @param {string} props.className - Additional CSS classes
 * @param {boolean} props.showCard - Whether to wrap in card layout (default: true)
 * @returns {JSX.Element} BreederSection component
 */
export const BreederInfoInput = ({ 
  totalBirdsForRace = '',
  editedData = {},
  participant, 
  onMessage, 
  onParticipantUpdate, 
  isEditing, 
  onChange,
  onEditingChange, 
  saving, 
  setSaving,
  className = '',
  showCard = true
}) => {

  // ==================== STATE MANAGEMENT ====================

  // Local state
  const [breederData, setBreederData] = useState({
    totalBirds: ''
  });
  const [originalBreederData, setOriginalBreederData] = useState(null);
  const [isTotalBirdsSaved, setIsTotalBirdsSaved] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // ==================== INITIALIZATION ====================

  // Initialize breeder data
  useEffect(() => {
    if (participant) {
      const initData = {
        totalBirds: participant.breederInfo?.totalBirdsForRace || participant.totalBirds || ''
      };
      setBreederData(initData);
      if (!originalBreederData) {
        setOriginalBreederData(initData);
      }
      
      // Mark as saved if participant already has total birds
      if (participant.breederInfo?.totalBirdsForRace || participant.totalBirds) {
        setIsTotalBirdsSaved(true);
      }
      
      // Reset unsaved changes when loading fresh data
      setHasUnsavedChanges(false);
    }
  }, [participant]);

  // ==================== HELPER FUNCTIONS ====================

  const hasRole = (roleToCheck) => {
    if (Array.isArray(participant?.role)) {
      return participant.role.some(r => r.toLowerCase() === roleToCheck.toLowerCase());
    }
    return participant?.role?.toLowerCase() === roleToCheck.toLowerCase();
  };

  // ==================== BIRD CALCULATIONS ====================

  // Calculate required admin rotation birds
  const getRequiredAdminRotationBirds = () => {
    const totalBirds = parseInt(breederData.totalBirds) || 0;
    if (totalBirds >= 30) return 3;
    if (totalBirds >= 12) return 2;
    if (totalBirds >= 5) return 1;
    return 0;
  };

  // Calculate bird distribution
  const birdCalc = {
    total: parseInt(breederData.totalBirds) || 0,
    toRotation: getRequiredAdminRotationBirds(),
    remainingForHandlers: Math.max(0, (parseInt(breederData.totalBirds) || 0) - getRequiredAdminRotationBirds())
  };



  // ==================== EVENT HANDLERS ====================

  // Handle input changes
  const handleChange = (field, value) => {
    if (onChange) {
      onChange(field, value);
    }
  };


  // Handle save
  const handleSave = async () => {
    if (!hasUnsavedChanges) return;
    
    setSaving(true);
    
    try {
      const totalBirds = parseInt(breederData.totalBirds) || 0;
      
      // Validation
      if (totalBirds < 5) {
        onMessage('You need at least 5 birds to participate in the race.', 'error');
        setSaving(false);
        return;
      }
      
      // Update participant data
      const updatedParticipant = {
        ...participant,
        totalBirds: totalBirds,
        breederInfo: {
          ...participant.breederInfo,
          totalBirdsForRace: totalBirds
        }
      };
      
      const result = await SaveParticipant(updatedParticipant);
      
      if (result && result.success) {
        onMessage('Breeder information saved successfully!');
        setOriginalBreederData({ ...breederData });
        setHasUnsavedChanges(false);
        setIsTotalBirdsSaved(true);
        onParticipantUpdate(updatedParticipant);
      } else {
        onMessage(`Failed to save: ${result?.message || 'Unknown error'}`, 'error');
      }
    } catch (error) {
      onMessage(`Error saving breeder information: ${error.message}`, 'error');
    }
    
    setSaving(false);
  };

  // Handle cancel
  const handleCancel = () => {
    if (originalBreederData) {
      setBreederData({ ...originalBreederData });
      setHasUnsavedChanges(false);
    }
  };



  // ==================== RENDER SECTIONS ====================

  /**
   * Render breeder information section
   */
  const renderBreederInfo = () => (
    <>
      <div className="card shadow-sm mb-4">
        <div className="card-header bg-white py-3">
          <h5 className="mb-0" style={{ color: 'rgb(11, 61, 145)' }}>
            <i className="bi bi-egg me-2"></i>Breeder Information
          </h5>
        </div>

        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-6">
              <label className="form-label fw-bold">Total Birds</label>
              {isEditing ? (
                <input
                  type="text"
                  className="form-control"
                  value={editedData?.breederInfo?.totalBirdsForRace || ''}                  
                  onChange={e => onChange && onChange('breederInfo', { ...editedData.breederInfo, totalBirdsForRace: e.target.value })}

                />
              ) : (
                <input type="number" className="form-control" value={totalBirdsForRace || ''} disabled />
              )}
            </div>
          </div>
        </div>
      </div>

    {/* Bird Distribution Dashboard */}
    {birdCalc.total > 0 && (
      <div className="card shadow-sm mb-4">
        <div className="card-header bg-white py-3">
          <h6 className="mb-0 fw-bold" style={{ color: '#0b3d91' }}>
            <i className="bi bi-pie-chart-fill me-2"></i>
            Bird Distribution Dashboard
          </h6>
        </div>
        <div className="card-body">
          <div className="row g-3">
            {/* 1. Total Birds */}
            <div className="col-lg-4 col-md-6">
              <div className="card bg-light border h-100">
                <div className="card-body p-4">
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <div className="fs-3 fw-bold text-dark">{birdCalc.total}</div>
                      <div className="fw-medium text-dark">Total Birds declared for race</div>
                    </div>
                    <div className="bg-secondary bg-opacity-10 rounded p-2">
                      <i className="bi bi-grid-3x3 fs-4 text-secondary"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* 2. Total Birds for Rotation */}
            <div className="col-lg-4 col-md-6">
              <div className="card bg-light border h-100">
                <div className="card-body p-4">
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <div className="fs-3 fw-bold text-dark">{birdCalc.toRotation}</div>
                      <div className="fw-medium text-dark">Total Birds for Rotation</div>
                      <div className="small text-muted">Required admin rotation</div>
                    </div>
                    <div className="bg-secondary bg-opacity-10 rounded p-2">
                      <i className="bi bi-arrow-repeat fs-4 text-secondary"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* 3. Available for Handlers */}
            <div className="col-lg-4 col-md-6">
              <div className="card bg-light border h-100">
                <div className="card-body p-4">
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <div className="fs-3 fw-bold text-dark">{birdCalc.remainingForHandlers}</div>
                      <div className="fw-medium text-dark">Birds left to assign to Handlers</div>
                    </div>
                    <div className="bg-secondary bg-opacity-10 rounded p-2">
                      <i className="bi bi-plus-circle fs-4 text-secondary"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Rotation Rules Information */}
          {birdCalc.total >= 5 && (
            <div className="mt-3">
              <h6 className="fw-bold mb-2">Admin Rotation Rules:</h6>
              <ul className="mb-2">
                <li><strong>5-11 birds:</strong> 1 bird must go to admin rotation</li>
                <li><strong>12-29 birds:</strong> 2 birds must go to admin rotation</li>
                <li><strong>30+ birds:</strong> 3 birds must go to admin rotation</li>
              </ul>
              <p className="mb-1">• All non-specified birds will go into rotation</p>
              <p className="mb-1">• Maximum 5 birds per breeder to handler per entry</p>
              <p className="mb-0">• Rotation birds are assigned by administrators to available handlers</p>
            </div>
          )}

          {/* Status Messages */}
          {birdCalc.total > 0 && birdCalc.total < 5 && (
            <div className="mt-3 p-3 bg-warning bg-opacity-10 border-start border-warning border-3 rounded">
              <div className="d-flex align-items-center">
                <i className="bi bi-exclamation-triangle text-warning me-2"></i>
                <small className="text-dark">
                  <strong>You need to enter at least 5 birds</strong> to participate in the race
                </small>
              </div>
            </div>
          )}
        </div>
      </div>
    )}
    </>
  );

  /**
   * Render bird distribution dashboard
   */
  // ==================== RENDER CONTENT ====================

  const renderContent = () => {
    if (!hasRole('breeder')) {
      return (
        <div className="alert alert-info">
          <i className="bi bi-info-circle me-2"></i>
          You need to have the Breeder role to access this section. You can add the Breeder role in the Personal Info tab.
        </div>
      );
    }

    return (
      <>
        {renderBreederInfo()}
      </>
    );
  };

  // ==================== RENDER ====================

  if (showCard) {
    return (
      <div className={className}>
        {renderContent()}
      </div>
    );
  }

  return (
    <div className={className}>
      {renderContent()}
    </div>
  );
};

export default BreederInfoInput;